import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  verifyClientCode, isTempClientMode, hasSessionTempClientCode,
  setTempClientCode, getWebsiteUrl
} from '../../utils/tempClientHandler';
import {
  Eye, EyeOff, Loader2, Sun, Moon, User, Lock,
  Users, TrendingUp, ShieldCheck, ChevronLeft, ChevronRight,
  Bell, BellOff, BellRing, ArrowRight, KeyRound
} from 'lucide-react';
import { requestNotificationPermission } from '../../services/firebase';
import ForgotPassword from './ForgotPassword';
import './Login.css';

// ─── Carousel slides ──────────────────────────────────────
const SLIDES = [
  {
    icon: Users,
    title: 'Manage Leads Effortlessly',
    desc: 'Centralize every lead, assign teams, and track conversions from one clean dashboard.',
  },
  {
    icon: TrendingUp,
    title: 'Real-time Insights',
    desc: 'Make faster decisions with live reports, follow-up tracking and team performance analytics.',
  },
  {
    icon: ShieldCheck,
    title: 'Secure & Role-based',
    desc: 'Granular permissions keep your data safe — Admin, Team Leader, Sales Manager, Tele Caller.',
  },
];

const Login = () => {
  const { login } = useAuth();

  const [view, setView] = useState('login'); // 'login' | 'forgot'

  // Temp clients must resolve a tenant before signing in. When the session has
  // no client code yet (fresh tab, expired handoff), ask for it first.
  const [step, setStep] = useState(
    () => (isTempClientMode() && !hasSessionTempClientCode() ? 'client' : 'login')
  );
  const [clientCode, setClientCode] = useState('');

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // ─── Notification permission ───────────────────────────
  const notifSupported = typeof window !== 'undefined' && 'Notification' in window;
  const [notifPerm, setNotifPerm] = useState(() => notifSupported ? Notification.permission : 'unsupported');
  const [notifBusy, setNotifBusy] = useState(false);

  const handleEnableNotifications = async () => {
    if (!notifSupported || notifPerm === 'granted' || notifBusy) return;
    setNotifBusy(true);
    try {
      await requestNotificationPermission();
    } catch (e) {}
    setNotifPerm(Notification.permission);
    setNotifBusy(false);
  };

  // ─── Theme (light/dark) ────────────────────────────────
  const [theme, setTheme] = useState(() => localStorage.getItem('login_theme') || 'dark');
  useEffect(() => {
    localStorage.setItem('login_theme', theme);
  }, [theme]);
  const toggleTheme = () => setTheme(t => (t === 'dark' ? 'light' : 'dark'));

  // ─── Carousel auto-rotate ──────────────────────────────
  const [slideIdx, setSlideIdx] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setSlideIdx(i => (i + 1) % SLIDES.length), 4500);
    return () => clearInterval(id);
  }, []);
  const nextSlide = () => setSlideIdx(i => (i + 1) % SLIDES.length);
  const prevSlide = () => setSlideIdx(i => (i - 1 + SLIDES.length) % SLIDES.length);

  const handleVerifyClient = async (e) => {
    e.preventDefault();
    const code = clientCode.trim();
    if (!code) { setError('Please enter your client code'); return; }
    setLoading(true);
    setError('');
    try {
      const verified = await verifyClientCode(code);
      setTempClientCode(verified);
      setStep('login');
    } catch (err) {
      setError(err.message || 'Invalid client code. Please check and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!username.trim()) { setError('Please enter username'); return; }
    if (!password) { setError('Please enter password'); return; }
    setLoading(true);
    setError('');
    const result = await login(username, password);
    setLoading(false);
    if (!result.success) {
      setError(result.message);
    }
  };

  const ActiveIcon = SLIDES[slideIdx].icon;

  return (
    <div className={`login-page login-page--${theme}`}>
      {/* Theme toggle */}
      <button
        type="button"
        className="login-theme-toggle"
        onClick={toggleTheme}
        aria-label="Toggle theme"
        title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
      >
        {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
      </button>

      <div className="login-shell">
        {/* ───── Left: Carousel / Info panel ───── */}
        <div className="login-info">
          {/* Decorative glow blobs */}
          <div className="login-info__blob-a" />
          <div className="login-info__blob-b" />

          <div className="login-info__brand">
            <div className="login-logo">
              <img src={`${process.env.PUBLIC_URL}/favicon-dark.ico`} alt="LeaDBoat" />
            </div>
            <span className="login-info__brand-name">LeaDBoat</span>
          </div>

          <div className="login-info__slider">
            <div className="login-info__slide" key={slideIdx}>
              <div className="login-info__slide-icon">
                <ActiveIcon size={32} />
              </div>
              <h2 className="login-info__slide-title">{SLIDES[slideIdx].title}</h2>
              <p className="login-info__slide-desc">{SLIDES[slideIdx].desc}</p>
            </div>

            <div className="login-info__controls">
              <button type="button" className="login-info__arrow" onClick={prevSlide} aria-label="Previous">
                <ChevronLeft size={18} />
              </button>
              <div className="login-info__dots">
                {SLIDES.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    className={`login-info__dot ${i === slideIdx ? 'is-active' : ''}`}
                    onClick={() => setSlideIdx(i)}
                    aria-label={`Slide ${i + 1}`}
                  />
                ))}
              </div>
              <button type="button" className="login-info__arrow" onClick={nextSlide} aria-label="Next">
                <ChevronRight size={18} />
              </button>
            </div>
          </div>

          <div className="login-info__footer">
            © {new Date().getFullYear()} LeaDBoat. All rights reserved.
          </div>
        </div>

        {/* ───── Right: Sign-in form ───── */}
        <div className="login-card">
          <div className="login-logo login-logo--mobile">
            <img src={`${process.env.PUBLIC_URL}/favicon-dark.ico`} alt="LeaDBoat" />
          </div>

          {view === 'forgot' ? (
            <ForgotPassword onBackToLogin={() => { setView('login'); setError(''); }} />
          ) : (
          <>
          <div className="login-header">
            <h1 className="login-title">
              {step === 'client' ? 'Enter your client code' : 'Welcome back'}
            </h1>
            <p className="login-subtitle">
              {step === 'client'
                ? 'Your client code identifies your company workspace'
                : 'Continue to your LeaDBoat dashboard'}
            </p>
          </div>

          {error && <div className="login-error">{error}</div>}

          {step === 'client' ? (
            <form onSubmit={handleVerifyClient} className="login-form">
              <div className="login-field">
                <label className="login-label">Client Code</label>
                <div className="login-input-wrap">
                  <KeyRound size={16} className="login-input__leading" />
                  <input
                    className="login-input login-input--has-icon"
                    type="text"
                    inputMode="numeric"
                    maxLength={8}
                    placeholder="Enter client code"
                    value={clientCode}
                    onChange={e => { setClientCode(e.target.value.replace(/\D/g, '')); setError(''); }}
                    autoFocus
                  />
                </div>
              </div>
              <button className="login-btn" type="submit" disabled={loading}>
                {loading ? <Loader2 size={18} className="spin" /> : <ArrowRight size={18} />}
                <span>{loading ? 'Verifying...' : 'Continue'}</span>
              </button>

              <div className="login-footer-link">
                <span className="login-resend__muted">Don't have a client code? </span>
                <a
                  className="login-resend__btn"
                  href={getWebsiteUrl()+"/get-started"}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Get one here
                </a>
              </div>
            </form>
          ) : (

          <form onSubmit={handleLogin} className="login-form">
            <div className="login-field">
              <label className="login-label">Username</label>
              <div className="login-input-wrap">
                <User size={16} className="login-input__leading" />
                <input
                  className="login-input login-input--has-icon"
                  type="text"
                  placeholder="Enter username"
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                  autoFocus
                />
              </div>
            </div>
            <div className="login-field">
              <label className="login-label">Password</label>
              <div className="login-input-wrap">
                <Lock size={16} className="login-input__leading" />
                <input
                  className="login-input login-input--has-icon"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  className="login-eye"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <button className="login-btn" type="submit" disabled={loading}>
              {loading && <Loader2 size={18} className="spin" />}
              <span>{loading ? 'Signing in...' : 'Sign In'}</span>
            </button>

            <div className="login-footer-link">
              <span className="login-resend__muted">Forgot your password? </span>
              <button
                type="button"
                className="login-resend__btn"
                onClick={() => { setView('forgot'); setError(''); }}
              >
                Click here
              </button>
            </div>

            {notifSupported && (
              <button
                type="button"
                className={`login-notif login-notif--${notifPerm}`}
                onClick={handleEnableNotifications}
                disabled={notifPerm === 'granted' || notifPerm === 'denied' || notifBusy}
                title={
                  notifPerm === 'granted' ? 'Notifications enabled'
                  : notifPerm === 'denied' ? 'Blocked — enable in browser settings'
                  : 'Allow notifications to receive lead alerts'
                }
              >
                {notifPerm === 'granted' ? <BellRing size={16} /> : notifPerm === 'denied' ? <BellOff size={16} /> : <Bell size={16} />}
                <span>
                  {notifPerm === 'granted' ? 'Notifications enabled'
                   : notifPerm === 'denied' ? 'Notifications blocked'
                   : notifBusy ? 'Requesting...' : 'Allow notifications'}
                </span>
              </button>
            )}

            {isTempClientMode() && (
              <div className="login-footer-link">
                <button
                  type="button"
                  className="login-resend__btn"
                  onClick={() => { setStep('client'); setClientCode(''); setError(''); }}
                >
                  Change client code
                </button>
              </div>
            )}
          </form>

          )}
          </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
